/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom } from 'jotai';
import type { StyleSpecification } from 'maplibre-gl';

/**
 * Attribution strings of the currently rendered basemap style, kept in sync by
 * the Map component on every `styledata` event so a basemap switch is always
 * reflected. Displayed in the InfoModal instead of an always-on map control.
 */
export const mapAttributionAtom = atom<string[]>([]);

/**
 * Extract unique plain-text attribution strings from a MapLibre style's sources.
 *
 * Source attributions may contain HTML (OSM-style `<a>` links); DOMParser
 * extraction reduces them to text without executing or injecting any markup.
 */
export function extractAttributions(style: StyleSpecification | undefined): string[] {
  if (!style?.sources) return [];
  const parts = new Set<string>();
  for (const source of Object.values(style.sources)) {
    if (!('attribution' in source) || typeof source.attribution !== 'string') continue;
    const text =
      new DOMParser().parseFromString(source.attribution, 'text/html').body.textContent ?? '';
    const trimmed = text.trim();
    if (trimmed) parts.add(trimmed);
  }
  return Array.from(parts);
}
